import { useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import Raven from 'raven-js';
import { apiNameToTrialIds } from '../../../constants/trial';
import { useGlobalAlertStore } from '../../../hooks/useGlobalAlertStore';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useStartTrial } from '../../../hooks/useStartTrial';
import { useTrialState } from '../../../hooks/useTrialState';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { trackPurchaseMotionFailure, trackTrialActivation } from '../../../utils/metrics';
import { handleTrialStartSuccess } from '../../../utils/trials';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { ActivationErrorModal } from './ActivationErrorModal';
import { ActivationSuccessModalContainer } from './ActivationSuccessModalContainer';
import { ConflictingTrialToolTip } from './ConflictingTrialToolTip';
import { RequestTrialModal } from './RequestTrialModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TrialButtonBase = ({
  apiName,
  trackingData,
  uiButtonProps,
  children,
  upgradeConfig,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  onClick,
  onTrialStartSuccess,
  isUpgradePoint
}) => {
  const {
    error,
    loading,
    trialState: {
      conflictingTrials,
      activeTrials,
      expiredTrials
    }
  } = useTrialState(apiName);
  const {
    user
  } = useUserInfo();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const {
    alertStore,
    shouldRenderGlobalList
  } = useGlobalAlertStore();
  const trialId = apiNameToTrialIds[apiName];
  const trialTrackingProperties = useMemo(() => ({
    activeTrials: [...activeTrials],
    expiredTrials: [...expiredTrials],
    isMultiTrial: activeTrials.size > 1,
    trialId
  }), [activeTrials, expiredTrials, trialId]);
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig,
    sourceKey: 'TRIAL'
  }), [apiName, trackingData, upgradeConfig]);
  const [startTrial, {
    data,
    error: startTrialError,
    loading: startTrialLoading
  }] = useStartTrial();
  const isAdmin = useIsAdmin();
  const internalRef = useRef(null);
  const buttonRef = (uiButtonProps === null || uiButtonProps === void 0 ? void 0 : uiButtonProps.buttonRef) || internalRef;
  useIntersectionObserver({
    targetRef: !loading ? buttonRef : undefined,
    onIntersect: () => {
      track('trial-activation-button', Object.assign({
        action: 'viewed'
      }, trackingProperties, trialTrackingProperties));
      if (isUpgradePoint) {
        track('interaction', Object.assign({
          action: 'viewed trial activation'
        }, trackingProperties));
      }
    }
  });
  useEffect(() => {
    if (startTrialError) {
      trackPurchaseMotionFailure({
        uniqueId: trackingData.uniqueId,
        apiName,
        purchaseMotion: 'TRIAL'
      });
      track('trial-modal', Object.assign({
        action: 'opening activation error modal'
      }, trackingProperties, trialTrackingProperties));

      // ********** PUBLIC EVENT **********
      // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
      // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
      // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation
      track('trial', Object.assign({
        action: 'activation failed'
      }, trackingProperties, trialTrackingProperties));
      Raven.captureException(startTrialError, {
        extra: Object.assign({}, trackingProperties, trialTrackingProperties),
        fingerprint: ['trialActivationFailed']
      });
    }
  }, [startTrialError, apiName, trackingData.uniqueId, trackingProperties, trialTrackingProperties]);
  useEffect(() => {
    if (data) {
      setShowSuccessModal(true);
      track('trial-modal', Object.assign({
        action: 'opening activation success modal'
      }, trackingProperties, trialTrackingProperties));
    }
  }, [data, trackingProperties, trialTrackingProperties]);
  if (startTrialLoading || loading || !user) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: true,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.trial"
      })
    }));
  }
  const handleClick = () => {
    onClick === null || onClick === void 0 || onClick('TRIAL');
    track('trial-activation-button', Object.assign({
      action: 'clicked'
    }, trackingProperties, trialTrackingProperties));
    if (isUpgradePoint) {
      track('interaction', Object.assign({}, trackingProperties));
    }
    if (isAdmin) {
      void startTrial({
        apiName: apiName,
        source: trackingData.source,
        onSuccess: onTrialStartSuccess
      }).then(createdTrials => {
        var _createdTrials$;
        const trialName = createdTrials === null || createdTrials === void 0 || (_createdTrials$ = createdTrials[0]) === null || _createdTrials$ === void 0 ? void 0 : _createdTrials$.trial.name;
        trackTrialActivation({
          app: trackingData.app,
          apiName,
          trialName: trialName !== null && trialName !== void 0 ? trialName : ''
        });
        track('trial-modal', Object.assign({
          action: 'opened modal container'
        }, trackingProperties, trialTrackingProperties));

        // ********** PUBLIC EVENT **********
        // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
        // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
        // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation

        track('trial', Object.assign({
          action: 'activation succeeded'
        }, trackingProperties, trialTrackingProperties));
      });
    } else {
      setAdminModalOpen(true);
    }
  };
  const handleStartSuccess = () => {
    const source = `${trackingData.app}-${trackingData.screen}-${trackingData.uniqueId}`;
    handleTrialStartSuccess({
      apiName: apiName,
      source
    });
  };
  const handleContinueClick = () => {
    track('trial-modal', Object.assign({
      action: 'clicked continue button'
    }, trackingProperties, trialTrackingProperties));
    handleStartSuccess();
  };
  const hideSuccessModal = () => {
    track('trial-modal', Object.assign({
      action: 'clicked close button'
    }, trackingProperties, trialTrackingProperties));
    setShowSuccessModal(false);
  };
  const handleTrialAdminModalClose = () => {
    setAdminModalOpen(false);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderGlobalList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "global-floating-alert-list",
      alertStore: alertStore
    }), !!startTrialError && /*#__PURE__*/_jsx(ActivationErrorModal, {
      apiName: apiName,
      error: startTrialError,
      onStartSuccess: handleStartSuccess
    }), showSuccessModal && /*#__PURE__*/_jsx(ActivationSuccessModalContainer, {
      apiName: apiName,
      hideModal: hideSuccessModal,
      upgradeConfig: upgradeConfig,
      onContinueClick: handleContinueClick
    }), adminModalOpen && /*#__PURE__*/_jsx(RequestTrialModal, {
      alertStore: alertStore,
      apiName: apiName,
      trackingData: trackingData,
      trialId: trialId,
      upgradeConfig: upgradeConfig,
      onClose: handleTrialAdminModalClose
    }), /*#__PURE__*/_jsx(ConflictingTrialToolTip, {
      apiName: apiName,
      conflictingTrials: conflictingTrials,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        onClick: handleClick,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        truncate: true,
        truncateStringProps: {
          justifyContent: 'center'
        }
      }, uiButtonProps, {
        buttonRef: buttonRef,
        disabled: (uiButtonProps === null || uiButtonProps === void 0 ? void 0 : uiButtonProps.disabled) || !!error || !!conflictingTrials.length,
        children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.trial"
        })
      }))
    })]
  });
};
export const TrialButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.TRIAL_BUTTON,
  children: /*#__PURE__*/_jsx(TrialButtonBase, Object.assign({}, props))
});